<template> 
  <b-modal ref="modal" :title="client.id ? `Edit Client ${client.name}` : 'Create Client'" lazy>
    <b-form @submit.prevent="submit">
      <validation-summary :errors="errors"></validation-summary>

      <v-form-group v-if="!client.id && isAdmin" label="Role">
        <v-select slot-scope="{ attrs, listeners }"
                  v-bind="attrs"
                  v-on="listeners"
                  id="role"
                  :class="[{ 'vs--has-value': selectedRole }]"
                  v-model="selectedRole"
                  :options="clientRoleOptions"
                  :clearable="false"
                  :filterable="false"
                  :searchable="false"
                  placeholder="Client Role" />
      </v-form-group>

      <template v-if="selectedRole && selectedRole.value === role.Client">
        <v-form-group :validator="$v.client.type" label="Client Type">
          <v-select slot-scope="{ attrs, listeners }"
                    v-model="client.type"
                    v-bind="attrs"
                    v-on="listeners"
                    :options="clientTypeFilterOptions"
                    placeholder="Client Type"
                    :clearable="false"
                    :filterable="false"
                    :searchable="false"
                    :class="[{ 'vs--has-value': client.type !== null }]"
                    id="clientType"
                    @input="validateProviderFields()" />
        </v-form-group>
        <v-form-group :validator="$v.client.name" label="Name">
          <b-form-input v-model="client.name"
                        placeholder="Name"
                        slot-scope="{ attrs, listeners }"
                        v-bind="attrs"
                        v-on="listeners" />
        </v-form-group>
        <v-form-group :validator="$v.client.ownerFirstName" label="Owner First Name">
          <b-form-input slot-scope="{ attrs, listeners }"
                        v-bind="attrs"
                        v-on="listeners"
                        v-model="client.ownerFirstName"
                        placeholder="First Name" />
        </v-form-group>
        <v-form-group :validator="$v.client.ownerLastName" label="Owner Last Name">
          <b-form-input slot-scope="{ attrs, listeners }"
                        v-bind="attrs"
                        v-on="listeners"
                        v-model="client.ownerLastName"
                        placeholder="Last Name" />
        </v-form-group>
        <v-form-group :validator="$v.client.ownerEmail" label="Owner Email">
          <b-form-input slot-scope="{ attrs, listeners }"
                        v-bind="attrs"
                        v-on="listeners"
                        v-model="client.ownerEmail"
                        type="email"
                        placeholder="Email" />
        </v-form-group>

        <v-form-group :validator="$v.client.phoneNumber" label="Owner Phone Number">
          <form-input-formatted type="phone"
                                id="phone"
                                v-model="client.phoneNumber"
                                placeholder="Enter phone number" />
        </v-form-group>

        <v-form-group :validator="$v.client.physicianFirstName" label="Prescriber First Name">
          <b-form-input slot-scope="{ attrs, listeners }"
                        v-bind="attrs"
                        v-on="listeners"
                        v-model="client.physicianFirstName"
                        placeholder="Prescriber First Name"
                        @input="validateProviderFields" />
        </v-form-group>

        <v-form-group :validator="$v.client.physicianLastName" label="Physician Last Name">
          <b-form-input slot-scope="{ attrs, listeners }"
                        v-bind="attrs"
                        v-on="listeners"
                        v-model="client.physicianLastName"
                        placeholder="Physician Last Name"
                        @input="validateProviderFields" />
        </v-form-group>

        <v-form-group :validator="$v.client.physicianNPI" label="Physician NPI">
          <b-form-input slot-scope="{ attrs, listeners }"
                        v-bind="attrs"
                        v-on="listeners"
                        v-model="client.physicianNPI"
                        placeholder="Physician NPI"
                        @input="validateProviderFields" />
        </v-form-group>

        <b-form-group class="form-group-radio" :validator="$v.client.billingMethod" label="Billing Method">
          <b-form-radio v-model="client.billingMethod" :value="billingMethodType.ClientBilling">Client-billing</b-form-radio>
          <b-form-radio v-model="client.billingMethod" :value="billingMethodType.IndividualBilling">Individual-billing</b-form-radio>
        </b-form-group>
      </template>
      <template v-else>
        <v-form-group :validator="$v.email" label="Email">
          <b-form-input slot-scope="{ attrs, listeners }"
                        v-bind="attrs"
                        v-on="listeners"
                        v-model="email"
                        placeholder="Email" />
        </v-form-group>
        <v-form-group :validator="$v.selectedClients" label="Clients">
          <v-select slot-scope="{ attrs, listeners }"
                    v-bind="attrs"
                    v-on="listeners"
                    id="role"
                    :class="[{ 'vs--has-value': selectedClients }]"
                    v-model="selectedClients"
                    :options="clientOptions"
                    placeholder="Clients"
                    multiple />
        </v-form-group>
      </template>
    </b-form>
    <div slot="modal-footer">
      <b-btn @click="cancel" variant="outline-secondary">Cancel</b-btn>
      <b-btn v-if="client.id && client.status === 'Pending'" @click="() => resendInviteEmail(client.id)" variant="outline-secondary" :loading="loading">Resend</b-btn>
      <b-btn :loading="loading" variant="success" @click="submit">
        {{ client.id ? 'Edit' : 'Create' }}
      </b-btn>
    </div>
  </b-modal>
</template>

<script>
  import { requiredIf, required, email, numeric } from 'vuelidate/lib/validators';

  import { createNamespacedHelpers } from 'vuex';
  const { mapActions, mapState } = createNamespacedHelpers('client');

  import billingMethodType from 'enums/billing-method.js';
  import clientType from 'enums/client-type.js';
  import role from 'enums/role.js';

  import adminService from 'services/admin-service';

  import modalFormMixin from 'mixins/modal-form-mixin';

  import _ from 'lodash';

  export default {
    mixins: [modalFormMixin],

    data() {
      return {
        billingMethodType,
        clientType,
        role,

        clientTypeFilterOptions: [
          { label: "Clinic", value: clientType.Clinic },
          { label: "Non Clinical", value: clientType.NonClinical }
        ],
        clientRoleOptions: [
          { label: "Client Admin", value: role.Client },
          { label: "Client Admin 2", value: role.ClientAdmin }
        ],
        clientOptions: [],

        selectedRole: null,
        selectedClients: null,
        email: null
      }
    },

    validations() {
      if (this.selectedRole && this.selectedRole.value === role.ClientAdmin) {
        return {
          selectedRole: {
            required
          },
          selectedClients: {
            required
          },
          email: {
            required,
            email
          }
        }
      }

      return {
        client: {
          name: {
            required
          },
          type: {
            required
          },
          ownerEmail: {
            required,
            email
          },
          ownerFirstName: {
            required
          },
          ownerLastName: {
            required
          },
          phoneNumber: {
            required
          },
          physicianFirstName: {
            required: requiredIf(function () { return this.providerValidation() })
          },
          physicianLastName: {
            required: requiredIf(function () { return this.providerValidation() })
          },
          physicianNPI: {
            required: requiredIf(function () { return this.providerValidation() }),
            numeric
          },
          billingMethod: {
            required
          }
        }
      }
    },

    computed: {
      ...mapState(['client']),
      ...mapState(['clients']),

      isClientAdmin() {
        return this.$auth.isInRole("ClientAdmin");
      },

      isAdmin() {
        return this.$auth.isInRole("Admin");
      }
    },

    methods: {
      ...mapActions(['loadClient', 'saveClient', 'resendInvite', 'getClientsForFilter']),

      async beforeShow(id) {
        await this.loadClient(id);

        if (this.client.id) {
          this.clientTypeFilterOptions.forEach(option => {
            if (option.value === this.client.type)
              this.client.type = option;
          });
        }

        if (this.isAdmin) {
          await this.getClientsForFilter();

          this.clientOptions = this.clients.sort((a, b) => a.label.localeCompare(b.label));
        }
        else if (this.isClientAdmin) {
          this.clientOptions = (await adminService.getClientsForClientAdmin()).map(c => {
            return {
              label: c.name,
              value: c.id
            }
          }).sort((a, b) => a.label.localeCompare(b.label));
        }

        this.selectedRole = this.clientRoleOptions.find(o => o.value === (this.isClientAdmin ? role.ClientAdmin : role.Client));
      },

      async resendInviteEmail(id) {
        await this.resendInvite(id);

        this.$root.successToast('Invite resent!');
      },

      async onSubmit() {
        if (this.selectedRole.value === role.Client) {
          let newClient = _.cloneDeep(this.client)

          newClient.type = this.client.type.value;

          await this.saveClient(newClient);
        }
        else {
          let newClientAdmin = {
            email: this.email,
            clientIds: this.selectedClients.map(c => parseInt(c.value))
          }

          await adminService.createClientAdmin(newClientAdmin);
        }
      },

      afterValidation() {
        if (!this.isClinicSelected() && this.isProviderFieldsEmpty())
          this.resetProviderValidation();
      },

      isClinicSelected() {
        if (this.client.type) {
          return this.client.type.value === Object.values(clientType)[clientType.Clinic - 1];
        }

        return true;
      },

      validateProviderFields() {
        if (this.isClinicSelected() || !this.isProviderFieldsEmpty()) {
          this.touchProviderValidation();
        }
        else {
          this.resetProviderValidation();
        }
      },

      resetProviderValidation() {
        this.$v.client.physicianFirstName.$reset();
        this.$v.client.physicianLastName.$reset();
        this.$v.client.physicianNPI.$reset();
      },

      touchProviderValidation() {
        this.$v.client.physicianFirstName.$touch();
        this.$v.client.physicianLastName.$touch();
        this.$v.client.physicianNPI.$touch();
      },

      isProviderFieldsEmpty() {
        return !this.client.physicianFirstName &&
               !this.client.physicianLastName &&
               !this.client.physicianNPI;
      },

      providerValidation() {
        if (this.isClinicSelected())
          return this.isClinicSelected();
        else
          return !this.isProviderFieldsEmpty();
      }
    },
  }

</script>